import React, {
    Component,
    useState
} from 'react';
import memoize from 'memoize-one';
import {ExtendTreeNode} from '~/shared/src/utilities/modules/tree';
import TreeUtils, {
    NodeMap,
    ReferenceDescription,
    NodePermissions
} from '~/frontend/src/utilities/TreeUtils';
import _ from 'lodash';
import {
    Tree,
    TreeExpandedKeysType,
    TreeDragDropEvent,
    TreeExpandedEvent,
    TreeDragStartParams,
    TreeDragEndParams,
    TreeTogglerTemplateOptions,
    TreeFilterTemplateOptions,
    TreeNodeTemplateOptions,
    TreeSelectionEvent,
    TreeNodeDoubleClickEvent
} from 'primereact/tree';
import {localeOption} from 'primereact/api';
import {Ripple} from 'primereact/ripple';
import {classNames} from 'primereact/utils';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';

import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';

import openPanel, {OpenPanel} from '~/frontend/src/actions/panelManager/openPanel';
import openPanelForItemType, {OpenPanelForItemType} from '~/frontend/src/actions/panelManager/openPanelForItemType';
import * as PanelActions from '~/frontend/src/actions/panel/common';

import * as TreeActions from '~/frontend/src/actions/panel/treeView';

import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import deleteItem, {DeleteItem} from '~/frontend/src/actions/data/deleteItem';
import updateItem, {UpdateItem} from '~/frontend/src/actions/data/updateItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import deleteReference, {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import updateReference, {UpdateReference} from '~/frontend/src/actions/data/updateReference';

import {ContextMenu} from 'primereact/contextmenu';
import Reference, {
    ReferenceAttributes,
    ReferenceAttributesForCreation
} from '~/shared/src/ci/Reference';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import loadData, {LoadData} from '~/frontend/src/actions/data/loadData';
import {
    store,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import {
    ElusiveData,
    ClearElusiveData,
    SetElusiveData
} from '~/frontend/src/actions/elusiveData';
import {
    ConfirmPopup,
    confirmPopup
} from 'primereact/confirmpopup';
import {__} from '~/shared/src/utilities/messageCatalog';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';
import Item, {
    ItemAttributesForCreation,
    ItemCollection
} from '~/shared/src/ci/Item';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {
    SetMessage,
    unsetMessage,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import {MergeMode} from '~/frontend/src/utilities/dataUtils';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import TreeNode from 'primereact/treenode';
import {Tooltip} from 'primereact/tooltip';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {Button} from 'primereact/button';
import keyboardUtils, {DropEffect} from '~/frontend/src/utilities/keyboardUtils';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import setPanelProps from '~/frontend/src/actions/panelManager/setPanelProps';
import {Function} from '~/shared/src/utilities/function';
import {
    Action,
    getResolvedConfigs
} from '~/shared/src/utilities/action';

import ActionMixin, {
    ActionMixinInterface,
    ClonedObjectsIdMappings,
    ResolvedDataObjects
} from '~/frontend/src/utilities/ActionMixin';
import parse from 'html-react-parser';
import objectUtils from '~/shared/src/utilities/objectUtils';
import SearchField from '~/frontend/src/components/base/shared/field/SearchField';
import ContentButton, {ReferenceType as ContentButtonType} from '~/frontend/src/components/base/shared/ContentButton';
import getAddItemDialog, {
    ComponentProps as AddTreeNodeDialogProps
} from '~/frontend/src/components/base/shared/tree/ItemTree/AddTreeNodePanel';
import {Panel} from 'primereact/panel';
import {keys} from 'ts-transformer-keys';
import {markFilterSearchesInText} from '~/frontend/src/utilities/renderHelpers';


const defaultProps = {
    /**
     * The database schema to filter data and metadata for.
     */
    schema: 'DATA' as Schema,

    /**
     * The status to filter data for.
     */
    status: 'active' as Status,

    /**
     * The timestamp to filter data at.
     */
    timestamp: 'now' as string,

    /**
     * The timezone to filter data at.
     */
    timezone: 'Europe/Berlin' as string,

    apiParams: {} as { [ key : string ] : string | number | boolean },

    // props to be passed to ItemTree
    selectedKey               : '' as string,
    selectedNodeId            : 0 as number,
    selectedPayloadId         : 0 as number,
    selectedPayloadReferenceId: 0 as number,
    expandedKeys              : {} as TreeExpandedKeysType,

    disableLoader: false as boolean,

    useElusiveDataFromDataContext: true as boolean,

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts: [] as string[],

    scrollTop : 0 as number,
    scrollLeft: 0 as number,

    /**
     * set to true if existing data should be kept on reload.
     * Needed if multiple trees are used in a single context, e.g. ViewDesigner
     */
    keepExistingOnReload: false as boolean
};

type DefaultProps = Readonly<typeof defaultProps>;

type Variables = {
    [ variable : string ] : any;
};

type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    variables? : Variables;
};

type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    className : string;

    style : CssStyle;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    title? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    tabTitle? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    tooltipTitle? : string;

    /**
     * The icon to display the panel with. If not set the title will be computed.
     */
    iconCls? : string;

    /**
     * Event fired when selecting a tree node representing an Item
     */
    onSelectItem? : ( selectedKey : string, selectedNodeId : number, selectedPayloadId : number, event : TreeSelectionEvent, parentKey : string, parentItemId : number, uid : string ) => void;
    onGetTreeUtils? : ( uid : string, treeUtils : TreeUtils ) => void;
    onTreeDataChange? : ( treeData : TreeNodeData[] | undefined ) => void;
    onTreeSelectionChange? : ( selectedKey : string ) => void;
    onTreeToggleNode? : ( expandedKeys : TreeExpandedKeysType ) => void;
    onLoad? : () => void;
    onDataLoad? : ( responseData : any ) => void;

    /**
     * Function invoked if form should render a new item (rootItemID < 0) and no global.data therefor existing.
     */
    onInvalidData? : () => void;

    // changing treeUtils stuff for performance boost on switching tabs.
    treeData? : TreeNodeData[];
    flatNodeMap? : NodeMap;
    flatDataObjectNodeMap? : NodeMap;
    payloadIdToDynamicNodeIdMap? : { [ p : number ] : string[] };

    subPanelErrors? : { [ subPanelKey : string ] : ErrorData };

    dirtyNodeIds? : string[];

    nodeIdToPanelKeyMap? : { [ nodeId : string ] : string };

    resumedNodes? : { [ nodeId : string ] : boolean };

    filter? : boolean;

    filterValue? : string;

    lastSelectionParameters? : {
        selectedKey : string;
        selectedNodeId : number;
        selectedPayloadId : number;
        selectedPayloadReferenceId : number;
    };

    /**
     * The transformer for the module (component) state.
     * NOTE: Have to be set (in opposition to be a default) to be included in the state anyway!
     */
    moduleTransformer? : string;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

const Actions = Object.assign( { loadData, unsetMessage, openPanel, openPanelForItemType }, PanelActions, TreeActions, IntercomActions, ElusiveDataActions, { addItem, deleteItem, updateItem, addReference, deleteReference, updateReference } );
type SetParam = <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof AllProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<AllProps & any> ) => SetParamsAction;


/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {

    // panel manager actions
    // DO NOT REMOVE - not necessarily used in this file BUT needed by ActionMixin (ActionMixinInterface)
    openPanel : OpenPanel;
    openPanelForItemType : OpenPanelForItemType;

    addItem : AddItem;
    deleteItem : DeleteItem;
    updateItem : UpdateItem;
    addReference : AddReference;
    deleteReference : DeleteReference;
    updateReference : UpdateReference;

    // intercom actions
    setMessage : SetMessage;
    unsetMessage : UnsetMessage;

    setElusiveData : SetElusiveData;
    clearElusiveData : ClearElusiveData;
    loadData : LoadData;
    unsetParam : UnsetParam;
    setParams : SetParams;
}

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    setParam : SetParam;
};

export type AllProps = DefaultProps & Props & StateProps & ActionProps;

type ContextMenuConfig = Partial<AddTreeNodeDialogProps> & NodePermissions;

const initialState = {
    // the ciId of the currently selected tree node
    ciId                 : false as boolean | number,
    clearData            : false as boolean,
    contextMenuNode      : '' as string,
    lastContextMenuNode  : '' as string,
    nodeToCopy           : '' as string,
    nodeToMove           : '' as string,
    addMode              : 'new' as ( 'new' | 'existing' | 'clone' ),
    nodeIdToErrorDataMap : {} as {
        [ nodeId : string ] : ErrorData | undefined
    },
    metaDataContext      : '' as string,
    addItemDialogConfig  : {} as ContextMenuConfig,
    deferRender          : false as boolean,
    runsInDesignerContext: false as boolean
};

type State = typeof initialState;

// define interface for ItemTree component to apply methods from mixin
export interface ItemTree extends Component<AllProps, State>, ActionMixinInterface {
}

/**
 * Tree component that renders a tree of Items
 */
export class ItemTree extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    state = Object.assign( {}, initialState );

    variables? : Variables;

    doSetScrollPosition : boolean = false;
    debouncedGetScrollPosition : () => void;

    debug : boolean = false;
    renderStartTime : number = 0;

    nodeButtons : { [ buttonId : string ] : { justInteracted : boolean, content? : ContentButtonType } } = {};
    addButtonContent : { [ nodeId : string ] : React.JSX.Element | null } = {};
    nodeButtonPanels : { [ panelId : string ] : Panel } = {};
    ignoreSelectionChange : boolean = false;

    getTreeUtils = memoize(
        function( uid : string, dataContext : string, elusiveData? : ElusiveData, user? : User ) {
            // TODO: Frage: Ist es gut in einem unauffälligen getter ein LoadData zu triggern?
            const treeUtils = new TreeUtils( uid, dataContext, elusiveData, this.props.flatNodeMap, this.props.flatDataObjectNodeMap, user, this.debug );
            // fire onLoad event if new data has been loaded
            // use timeout to decouple onLoad error to avoid potential state changes in render cycle
            setTimeout( () => {
                this.props.onGetTreeUtils?.( this.props.uid, treeUtils );
                if ( elusiveData ) {
                    this.props.onLoad?.();
                }
            }, 10 );
            return treeUtils;
        }.bind( this ), ( newArgs, lastArgs ) => {
            let retVal = newArgs[ 0 ] === lastArgs[ 0 ];
            retVal = retVal && _.isEqual( newArgs[ 2 ]?.metaData?.cypherConfig ?? {}, lastArgs[ 2 ]?.metaData?.cypherConfig ?? {} );
            return retVal;
        } );

    getPayloadTreeData = memoize(
        ( treeUtils : TreeUtils, payloadRootItemId : number, elusiveData? : ElusiveData, newPayloadReferenceId? : number, user? : User ) : TreeNodeData[] => {
            const expandedKeys = { ...this.props.expandedKeys ?? {} },
                  retVal       = treeUtils.applyData( payloadRootItemId, expandedKeys, newPayloadReferenceId );

            this.props.setParam?.( this.props.uid, 'expandedKeys', expandedKeys );

            return retVal;
        } );

    cm? : ContextMenu | null;

    treeUtils? : TreeUtils;

    tree? : Tree | null;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.onLoad = this.onLoad.bind( this );
        this.onSelectTreeNode = this.onSelectTreeNode.bind( this );
        this.onToggleTreeNode = this.onToggleTreeNode.bind( this );
        this.onDragStart = this.onDragStart.bind( this );
        this.onDragEnd = this.onDragEnd.bind( this );
        this.onValidateDrop = this.onValidateDrop.bind( this );
        this.onDrop = this.onDrop.bind( this );
        this.onDragOver = this.onDragOver.bind( this );
        this.onAddNewNode = this.onAddNewNode.bind( this );
        this.onNodeDoubleClick = this.onNodeDoubleClick.bind( this );
        this.addNode = this.addNode.bind( this );
        this.deleteOrUndeleteNode = this.deleteOrUndeleteNode.bind( this );
        this.nodeTemplate = this.nodeTemplate.bind( this );
        this.togglerTemplate = this.togglerTemplate.bind( this );
        this.filterTemplate = this.filterTemplate.bind( this );
        this.openNewPanel = this.openNewPanel.bind( this );
        //this.getTreeContextMenu = this.getTreeContextMenu.bind( this );

        this.debouncedGetScrollPosition = _.debounce( this.getScrollPosition.bind( this ), 250 );

        if ( this.props.treeData && global.data[ this.props.dataContext ]?.items ) {
            this.treeUtils = this.getTreeUtils( this.props.uid, this.props.dataContext, this.props.elusiveData, this.props.user );
            this.treeUtils.setData( this.props.apiParams.rootItemId as number, this.props.treeData, this.props.payloadIdToDynamicNodeIdMap );
        }

        // TODO: decide when to autoload data on component initialization => didMount
        if ( !this.props.disableLoader && ( _.isEmpty( this.props.elusiveData?.data ) || !global.data[ this.props.dataContext ] ) ) {
            const {
                      apiParams,
                      schema,
                      dataContext,
                      uid,
                      useElusiveDataFromDataContext
                  }          = this.props as AllProps,
                  rootItemId = apiParams.rootItemId as number ?? 0;

            if ( rootItemId <= 0 && !apiParams.rootItemKey && !global.data[ dataContext ]?.items[ rootItemId ] ) {
                if ( this.props.onInvalidData ) {
                    this.props.onInvalidData();
                }
            }
            else {
                void this.props.loadData?.(
                    'tree',
                    dataContext,
                    useElusiveDataFromDataContext
                        ? dataContext
                        : uid,
                    undefined,
                    schema,
                    {
                        mergeMode             : 'keepExisting' as MergeMode,
                        metaDataIncludesCiData: true,
                        extraDataKeys         : [ 'rootItemId', 'cypherConfig' ],
                        extraMetaDataKeys     : [ 'treeData', 'dataStructure', 'cypherConfig', 'rootItemId', 'dataObjectTree', 'dataObjectMapping', 'payloadSchema', 'isStopped' ],
                        params                : {
                            ...apiParams,
                            schema
                        }
                    },
                    undefined,
                    this.onLoad
                );
            }
        }

        if ( this.debug ) {
            this.renderStartTime = performance.now();
        }

    }

    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  subPanelErrors,
                  nodeIdToPanelKeyMap,
                  dataContext,
                  uid
              }                       = props,
              nodeIdToErrorDataMap    = nodeIdToPanelKeyMap ? _.mapValues( nodeIdToPanelKeyMap, panelKey => subPanelErrors?.[ panelKey ] ) : {},
              retVal : Partial<State> = {
                  nodeIdToErrorDataMap,
                  runsInDesignerContext: uid?.startsWith( 'ViewDesigner' ),
                  metaDataContext      : dataContext + '-meta'
              };

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.props.setParam( this.props.uid, 'moduleTransformer', 'tree' );

        if ( this.props.useElusiveDataFromDataContext ) {
            this.doSetScrollPosition = true;
            document.addEventListener( 'scroll', this.debouncedGetScrollPosition, true );
            this.setScrollPosition();
        }

        if ( this.tree?.getElement() ) {
            this.tree.getElement().onkeydown = this.onKeyDown.bind( this );
        }
        this.checkForIntercomMessages();
        if ( !this.props.treeData?.length || !global.data[ this.props.dataContext ]?.items ) {
            void this.buildTree();
        }

        this.props.onTreeDataChange?.( this.props.treeData );
        this.props.onTreeSelectionChange?.( this.props.selectedKey );
    }

    componentWillUnmount() {
        if ( this.props.useElusiveDataFromDataContext ) {
            document.removeEventListener( 'scroll', this.debouncedGetScrollPosition, true );
            this.getScrollPosition();
        }
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate() {
        this.checkForIntercomMessages();
        if ( this.props.useElusiveDataFromDataContext ) {
            this.setScrollPosition();
        }
    }

    shouldComponentUpdate( nextProps : Readonly<AllProps>, nextState : Readonly<State>, nextContext : any ) : boolean {
        const startTime    = this.debug ? performance.now() : 0,
              changedProps = objectUtils.getDiffKeys(
                  this.props,
                  nextProps,
                  [ 'treeData', 'flatNodeMap', 'flatDataObjectNodeMap' ],
                  undefined,
                  [ 'scrollTop', 'scrollTop', 'onGetTreeUtils', 'onInvalidData' ]
              ),
              changedState = objectUtils.getDiffKeys(
                  this.state,
                  nextState
              ),
              allowRebuild = !changedProps.some( prop => [ 'treeData', 'flatNodeMap', 'flatDataObjectNodeMap' ].includes( prop ) )
                  && !changedProps.every( prop => [ 'expandedKeys' ].includes( prop ) );

        let deferRender = nextState.deferRender;

        if ( this.debug && this.renderStartTime === 0 ) {
            this.renderStartTime = startTime;
        }

        // run tree functions if something relevant has been changed
        if ( allowRebuild && !changedState.includes( 'deferRender' ) && ( !!changedState.length || !!changedProps.length ) ) {

            // wrap function execution in a deferRender block
            const asyncBuild = async () => {
                await new Promise( resolve => {
                    this.setState( { deferRender: true }, () => resolve( true ) );
                } );

                await this.buildTree( nextProps, nextState );

                await new Promise( resolve => {
                    this.setState( { deferRender: false }, () => resolve( true ) );
                } );
            }

            if ( this.debug ) {
                console.log( '    asyncBuild' );
            }

            deferRender = true;
            void asyncBuild();
        }

        if ( !this.props.treeData?.[ 0 ] && changedProps.includes( 'treeData' ) && nextProps.treeData?.[ 0 ] ) {
            const startNode = nextProps.treeData?.[ 0 ];
            setTimeout( () => {
                void this.loadAllStoppedNodes( startNode, true );
            }, 10 );
        }

        const retVal = ( !deferRender || changedState.includes( 'addItemDialogConfig' ) || changedState.includes( 'addMode' ) ) && ( !!changedState.length || !!changedProps.length );

        if ( this.debug ) {
            console.log( '    deferRender', deferRender );
            console.log( '    changedProps', changedProps );
            console.log( '    stateChanged', changedState );
            console.log( '    retVal', retVal );
            console.log( `ItemTree - shouldComponentUpdate - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms`.asLogLine( 100 ) );
        }

        return retVal;
    }

    onLoad( responseData : any ) {
        const { dataContext } = this.props,
              rootItemId      = responseData?.data?.rootItemId ?? 0;

        const rootItem : Item | undefined = global.data[ dataContext ]?.items?.[ rootItemId ];

        this.props.onDataLoad?.( responseData );

        this.props.setParams( this.props.parentUid, {
            title       : responseData.data.title,
            tabTitle    : responseData.data.tabTitle,
            tooltipTitle: responseData.data.tooltipTitle,
            iconCls     : responseData.data.iconCls,
            saveConfig  : responseData.data.treeViewConfig?.saveConfig,
            viewName    : responseData.data.viewName,
            rootItemId  : rootItem?.id,
            rootItemKey : rootItem?.key,
            rootItemType: rootItem?.itemType.name
        } );
    }

    async buildTree( props : AllProps = this.props, state : State = this.state ) {
        const startTime = this.debug ? performance.now() : 0,
              {
                  uid,
                  dataContext,
                  elusiveData,
                  selectedPayloadReferenceId,
                  user
              }         = props,
              {
                  nodeIdToErrorDataMap
              }         = state,
              {
                  rootItemId
              }         = elusiveData?.data ?? {};

        this.treeUtils = this.getTreeUtils( uid, dataContext, elusiveData, user );

        const treeData = rootItemId && global.data[ dataContext ]?.items[ rootItemId ]
            // for cypherTree (used by TreeTest) tree will be loaded completely in backend, otherwise use treeUtils
            ? ( this.props.apiParams?.treeCypher ? elusiveData?.data?.treeData : this.getPayloadTreeData( this.treeUtils, rootItemId, elusiveData, !this.props.selectedKey ? selectedPayloadReferenceId : undefined, user ) )
            : elusiveData?.metaData?.treeData ?? [];

        this.treeUtils.applyErrorData( nodeIdToErrorDataMap );

        // check if functions exists for this tree
        const functions = this.treeUtils?.getFunctions();
        if ( functions?.length ) {

            // execute all functions
            await this.executeFunctions();

        }

        const newParams : Partial<Props> = {};
        let changed = false;

        if ( treeData !== props.treeData ) {
            this.props.onTreeDataChange?.( treeData );
            newParams.treeData = treeData;
            changed = true;
        }
        if ( this.treeUtils?.flatNodeMap !== props.flatNodeMap ) {
            newParams.flatNodeMap = this.treeUtils?.flatNodeMap;
            changed = true;
        }
        if ( this.treeUtils?.flatDataObjectNodeMap !== props.flatDataObjectNodeMap ) {
            newParams.flatDataObjectNodeMap = this.treeUtils?.flatDataObjectNodeMap;
            changed = true;
        }
        if ( this.treeUtils?.payloadIdToDynamicNodeIdMap !== props.payloadIdToDynamicNodeIdMap ) {
            newParams.payloadIdToDynamicNodeIdMap = this.treeUtils?.payloadIdToDynamicNodeIdMap;
            changed = true;
        }

        if ( changed ) {
            this.props.setParams( uid, newParams );
        }

        if ( this.debug ) {
            console.log( `buildTree - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms`.asLogLine( 50 ) );
        }

    }

    setScrollPosition() {
        if ( this.doSetScrollPosition ) {
            const { parentUid } = this.props;

            let scrollElement = document.getElementById( `${parentUid}_menuContentSplitter` )?.firstChild as HTMLElement | undefined;
            if ( scrollElement && !scrollElement.style?.overflowY ) {
                scrollElement = scrollElement.firstChild as HTMLElement | undefined;
            }

            if ( scrollElement ) {
                scrollElement.scrollTop = this.props.scrollTop;
                scrollElement.scrollLeft = this.props.scrollLeft;

                if ( ( this.props.scrollTop === 0 || scrollElement.scrollTop > 0 ) && ( this.props.scrollLeft === 0 || scrollElement.scrollLeft > 0 ) ) {
                    this.doSetScrollPosition = false;
                }
            }
        }
    }

    getScrollPosition() {
        if ( !this.doSetScrollPosition ) {
            const { uid, parentUid } = this.props;

            let scrollElement = document.getElementById( `${parentUid}_menuContentSplitter` )?.firstChild as HTMLElement | undefined;
            if ( scrollElement && !scrollElement.style?.overflowY ) {
                scrollElement = scrollElement.firstChild as HTMLElement | undefined;
            }

            if ( scrollElement ) {
                this.props.setParam( uid, 'scrollTop', scrollElement.scrollTop ?? 0 );
                this.props.setParam( uid, 'scrollLeft', scrollElement.scrollLeft ?? 0 );
            }
        }
    }

    /**
     * Execute a single function or action. Return false if an error occurred, true if all ok
     * @param actionOrFunction
     * @param resolvedDataObjects
     * @param popupTarget
     * @param affectedNodes
     */
    async executeActionOrFunction( actionOrFunction : Action | Function, resolvedDataObjects : ResolvedDataObjects, popupTarget : HTMLElement, affectedNodes? : NodeMap, triggerNode? : ExtendTreeNode ) : Promise<boolean> {
        let retVal = true;
        try {
            // build the function and execute it
            await this.buildDynamicFunction(
                {
                    ...this.getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects ),
                    ...getResolvedConfigs( actionOrFunction.configs ?? {} ),
                    ...( affectedNodes ? { nodes: affectedNodes } : undefined ),
                    ...( triggerNode ? {
                        trigger: {
                            id       : triggerNode.data.ciItem.id,
                            key      : triggerNode.data.ciItem.key,
                            name     : triggerNode.data.ciItem.name,
                            type     : triggerNode.cypherMatchNum ? 'DynamicNode' : 'TreeNode',
                            eventType: 'drop',
                            value    : undefined
                        }
                    } : undefined )

                },
                actionOrFunction.function ?? '',
                resolvedDataObjects
            )();
        }
        catch ( e ) {
            // error handling (display popup message and set retVal to false)
            const errorTitle  = `<span class="yn-error yn-confirmpopup-title">${__(
                      ( actionOrFunction as Action ).type ? 'YN.base.tree.executeAction.error.title' : 'YN.base.tree.executeFunction.error.title',
                      {
                          id  : String( actionOrFunction.id ) ?? '',
                          key : actionOrFunction.key ?? `order: ${actionOrFunction.order}`,
                          name: actionOrFunction.name ?? ''
                      }
                  )}</span>`,
                  errorDetail = this.getDynamicFunctionErrorDetail( e, actionOrFunction.function ?? '' );

            // wait on error
            await new Promise( ( resolve ) => {
                confirmPopup( {
                    target         : popupTarget,
                    message        : parse( `${errorTitle}<br /><br />${errorDetail}` ),
                    content        : <></>,
                    icon           : 'pi pi-exclamation-triangle yn-error',
                    className      : 'yn-itemtree-confirm-popup',
                    acceptLabel    : __( 'YN.base.common.ok' ),
                    acceptClassName: 'p-button p-button-outlined',
                    rejectClassName: 'hidden',
                    accept         : () => {
                        resolve( true );
                    }
                } );
            } );

            retVal = false;
        }

        return retVal;
    }

    /**
     * Execute all tree functions
     */
    async executeFunctions() {
        // TODO: node data for functions attached to TreeView directly ist not working correctly
        const functions = this.treeUtils?.getFunctions();
        if ( functions?.length ) {
            for ( const fnc of functions ) {
                for ( const nodeKey in fnc.formElements ) {
                    const nodes = this.treeUtils!.getNodesByOrigId( fnc.formElements[ nodeKey ] );
                    for ( const nodeId in nodes ) {
                        const node                = nodes[ nodeId ],
                              functionDataContext = `changeData_actions_${node.id}`,
                              resolvedDataObjects = this.treeUtils!.getResolvedDataObjects( node, fnc.dataObjects, functionDataContext );
                        // TODO: discuss and implement error handling
                        await this.executeActionOrFunction( fnc, resolvedDataObjects, this.tree!.getElement()!, nodes );
                        delete global.data[ functionDataContext ];
                    }
                }
            }
        }
    }

    /**
     * handler for actions triggered by the tree
     * @param dragNodeId
     * @param targetNodeId
     * @param originalEvent
     * @param props
     */
    async onTriggerAction( dragNodeId : string, targetNodeId : string, originalEvent : any, props : AllProps = this.props ) {
        const dragNode = this.treeUtils!.getNodeById( dragNodeId ),
              dropNode = this.treeUtils!.getNodeById( targetNodeId );
        if ( dragNode && dropNode ) {
            const actions                                           = this.treeUtils!.getNodeActions( dropNode ),
                  actionDataContext                                 = `changeData_actions_${dragNodeId}`,
                  clonedObjectsIdMappings : ClonedObjectsIdMappings = { items: {}, references: {} };

            let aborted : boolean = false;
            let allResolvedDataObjects : ResolvedDataObjects = {};

            for ( const action of actions ) {
                const resolvedDataObjects = this.treeUtils!.getResolvedDataObjects( dragNode, action.dataObjects, actionDataContext, dropNode );

                // merge in newly added items and references
                for ( const previouslyResolvedDataObjectKey of Object.keys( allResolvedDataObjects ) ) {
                    if ( resolvedDataObjects[ previouslyResolvedDataObjectKey ] ) {
                        resolvedDataObjects[ previouslyResolvedDataObjectKey ] = allResolvedDataObjects[ previouslyResolvedDataObjectKey ];
                    }
                }

                aborted = !( await this.executeActionOrFunction( action, resolvedDataObjects, originalEvent.currentTarget, undefined, dropNode ) );

                if ( aborted ) {
                    break;
                }

                allResolvedDataObjects = {
                    ...allResolvedDataObjects,
                    ...resolvedDataObjects
                };
            }

            // apply modifications
            if ( !aborted ) {

                try {
                    // obey additionalDataContexts
                    const subPanelKeys = ( Object.keys( store.getState().elusiveData ?? {} ) as string[] ).filter( key => key.match( `^${props.dataContext}[_-](.*)$` ) );

                    this.applyDynamicFunctionVariablesChanges( props );
                    this.applyDynamicFunctionDataChanges( actionDataContext, allResolvedDataObjects, clonedObjectsIdMappings, { ...props, additionalElusiveDataContexts: subPanelKeys } );
                }
                catch ( e ) {
                    console.error( e );
                }

            }

            delete global.data[ actionDataContext ];
        }
    }

    copyOrMoveNode( node : ExtendTreeNode, targetNode : ExtendTreeNode, dropEffect : DropEffect ) : boolean {
        let retVal = false;
        const { uid, dataContext, useElusiveDataFromDataContext } = this.props;
        if ( node && targetNode && this.treeUtils ) {
            const refConfig : ( ReferenceAttributes & {
                referenceTypeName? : string
            } )[] | boolean = this.treeUtils.checkDropTarget( node, targetNode, dropEffect );
            if ( refConfig ) {
                if ( refConfig.length === 1 ) {
                    if ( dropEffect === 'move' ) {
                        this.props.deleteReference?.( dataContext, node.data.payloadReferenceId, true, useElusiveDataFromDataContext ? undefined : [ uid ] );
                    }
                    this.props.addReference?.( dataContext, refConfig[ 0 ] as ReferenceAttributesForCreation, schema!, useElusiveDataFromDataContext ? undefined : [ uid ] );
                    const refId = Reference.uniqueIdCount + 1;
                    this.treeUtils?.updateDroppedNode( node, targetNode, refId );
                    retVal = true;
                }
            }
            else {
                retVal = true;
            }
        }
        return retVal;
    }

    onKeyDown( event : KeyboardEvent ) {
        if ( keyboardUtils.cmdKey( event ) && [ 'KeyC', 'KeyX', 'KeyV', 'KeyD', 'KeyO' ].includes( event.code ) ) {

            const {
                      uid,
                      selectedKey
                  } = this.props;

            const {
                      nodeToCopy,
                      nodeToMove
                  } = this.state;


            const selectedNode = this.treeUtils?.getNodeById( selectedKey as string );

            // if selected is a payload node
            if ( selectedNode?.data.payloadId ) {
                switch ( event.code ) {
                    // command/ctrl-C
                    case 'KeyC':
                        this.setState( { lastContextMenuNode: selectedNode.id, nodeToCopy: selectedNode.id, nodeToMove: '' } )
                        break;
                    // command/ctrl-X
                    case 'KeyX':
                        this.setState( { lastContextMenuNode: selectedNode.id, nodeToCopy: '', nodeToMove: selectedNode.id } )
                        break;
                    // command/ctrl-V
                    case 'KeyV':
                        // move
                        if ( nodeToMove ) {
                            // prevent move on same place
                            if ( nodeToMove !== selectedNode.id ) {
                                const node       = this.treeUtils?.getNodeById( nodeToMove ),
                                      targetNode = this.treeUtils?.getNodeById( selectedNode.id );
                                if ( node && targetNode ) {
                                    if ( this.copyOrMoveNode( node, targetNode, 'move' ) ) {
                                        // set selectedPayloadId to newly added item and panelKeys to an empty string
                                        this.props.setParams?.( uid, {
                                            selectedPayloadId         : node.data.payloadId,
                                            selectedPayloadReferenceId: node.data.payloadReferenceId,
                                            selectedKey               : '',
                                            selectedNodeId            : 0
                                        } );
                                    }
                                    else {
                                        this.addNode( 'clone', selectedNode.id, nodeToMove );
                                    }
                                }
                            }
                        }
                        // copy
                        else if ( nodeToCopy ) {
                            let targetNodeId = selectedNode.id;
                            if ( nodeToCopy === selectedNode.id ) {
                                targetNodeId = selectedNode.data.parentNodeId;
                            }
                            this.addNode( 'clone', targetNodeId, nodeToCopy );
                        }
                        break;
                    // command/ctrl-D
                    case 'KeyD':
                        if ( selectedNode.id && selectedNode.data.parentNodeId ) {
                            this.addNode( 'clone', selectedNode.data.parentNodeId, selectedNode.id );
                        }
                        event.preventDefault();
                        break;
                    // command/ctrl-O
                    case 'KeyO':
                        this.openNewPanel( event, selectedKey as string )
                        break;
                }
            }
        }
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( props : AllProps = this.props as AllProps ) {
        // check for toasts
        if ( props.intercomMessages?.notification?.reload ) {

            const {
                      apiParams,
                      dataContext,
                      uid,
                      schema,
                      useElusiveDataFromDataContext,
                      keepExistingOnReload
                  } = this.props;


            apiParams.rootItemId = apiParams.rootItemId ?? props.intercomMessages?.notification?.reload;
            apiParams.schema = schema;
            void this.props.loadData?.(
                'tree',
                dataContext,
                useElusiveDataFromDataContext
                    ? dataContext
                    : uid,
                undefined,
                schema,
                {
                    mergeMode             : keepExistingOnReload ? 'keepExisting' as MergeMode : undefined,
                    metaDataIncludesCiData: true,
                    extraDataKeys         : [ 'rootItemId', 'cypherConfig' ],
                    extraMetaDataKeys     : [ 'treeData', 'dataStructure', 'cypherConfig', 'rootItemId', 'dataObjectTree', 'dataObjectMapping', 'payloadSchema', 'isStopped' ],
                    params                : {
                        ...apiParams,
                        schema,
                        rootItemId: apiParams.rootItemId ?? props.intercomMessages?.notification?.reload
                    }
                },
                undefined,
                this.onLoad
            );

            // clear message
            this.props.unsetMessage?.( uid, 'notification', 'reload' );
        }
    }

    addNode( addMode : 'new' | 'existing' | 'clone', targetNodeId : string, sourceNodeId? : string ) {
        const {
                  dataContext
              }                      = this.props,
              targetNode             = this.treeUtils!.getNodeById( targetNodeId ),
              targetItemId           = this.treeUtils!.getPayloadIdForNode( targetNodeId ) ?? 0,
              targetItemTypeName     = global.data[ dataContext ]?.items[ targetItemId ]?.itemType.name,
              sourceItemId           = sourceNodeId ? this.treeUtils!.getPayloadIdForNode( sourceNodeId ) ?? 0 : 0,
              sourceItemTypeName     = global.data[ dataContext ]?.items[ sourceItemId ]?.itemType.name,
              allowedReferenceSetIds = this.treeUtils!.getAllowedCirsIdForNode( targetNodeId ),
              addItemDialogConfig    = this.getAddItemDialogConfig( targetItemTypeName, allowedReferenceSetIds, targetNode ),
              childNodes             = Object.values( this.treeUtils!.flatDataObjectNodeMap ?? {} ).filter( n => {
                  return n.data?.parentNodeId === targetNode?.origId;
              } ),
              labelKeys : string[]   = [];

        addItemDialogConfig.allowedReferenceSetIds = allowedReferenceSetIds;
        addItemDialogConfig.targetItemTypeName = targetItemTypeName;
        addItemDialogConfig.targetItemId = targetItemId;
        addItemDialogConfig.cloneItemTypeName = sourceItemTypeName;
        addItemDialogConfig.cloneItemId = sourceItemId;

        // get labelKeys from child nodes
        labelKeys.push( ...childNodes.reduce(
            ( acc, childNode ) => {
                const labelKey = childNode.labelKey
                    ? __( childNode.labelKey )
                    : '';

                if ( labelKey && !acc.includes( labelKey ) ) {
                    acc.push( labelKey );
                }

                return acc;
            },
            [] as string[]
        ) );

        // no labelKeys yet -> get displayAttribute from source ItemType
        if ( !labelKeys.length && global.data[ dataContext ]?.items[ sourceItemId ]?.itemType ) {
            labelKeys.push( `\$\{item.${global.data[ dataContext ].items[ sourceItemId ].itemType.displayAttribute}\}` );
        }

        // no labelKeys yet -> get displayAttributes from allowed ReferenceSets - ItemTypes
        if ( !labelKeys.length ) {
            labelKeys.push( ...Object.keys( allowedReferenceSetIds ).reduce(
                ( acc, rSetId ) => {
                    const referenceSet = metaModel[ global.data[ dataContext ].schema ].getReferenceSet( Number( rSetId ) );

                    let labelKey : string = referenceSet?.[ allowedReferenceSetIds[ rSetId ] === 'dest' ? 'srcItemType' : 'destItemType' ].displayAttribute ?? '';

                    if ( labelKey !== '' ) {
                        labelKey = `\$\{item.${labelKey}\}`;
                    }

                    if ( labelKey && !acc.includes( labelKey ) ) {
                        acc.push( labelKey );
                    }

                    return acc;
                },
                [] as string[]
            ) );
        }

        addItemDialogConfig.cloneItemLabel = labelKeys.length === 1 ? labelKeys[ 0 ] : undefined;

        this.setState( { addMode, nodeToMove: '', nodeToCopy: '', addItemDialogConfig } );

        return {
            ...this.state,
            addMode,
            nodeToMove: '',
            nodeToCopy: '',
            addItemDialogConfig
        };
    }

    /**
     * Delete existing node (set status to -1 for existing, remove unsaved nodes directly)
     * @param deleteReferenceOnly true to delete reference only
     * @param nodeId Node to delete. If not set, current node will be deleted
     * @param mode
     * @param noConfirmation
     */
    deleteOrUndeleteNode( deleteReferenceOnly : boolean = false, nodeId? : string, mode : 'single' | 'ancestor' = 'single', noConfirmation : boolean = false ) {
        const { uid, dataContext, useElusiveDataFromDataContext } = this.props,
              { contextMenuNode, lastContextMenuNode }            = this.state,
              deleteObjectType                                    = mode === 'single'
                  ? ( deleteReferenceOnly ? 'Reference' : 'Item' )
                  : ( deleteReferenceOnly ? 'DescendantReferences' : 'DescendantItems' ),
              nodesToDelete                                       = mode === 'single'
                  ? [ this.treeUtils!.getNodeById( nodeId ?? ( lastContextMenuNode || contextMenuNode ) ) ]
                  : this.treeUtils!.getChildNodesById( nodeId ?? ( lastContextMenuNode || contextMenuNode ) ),
              // allow undelete only for single mode
              firstPayloadId                                      = nodesToDelete[ 0 ]?.data.payloadId,
              firstPayloadItem                                    = global.data[ dataContext ]?.items[ firstPayloadId ],
              firstPayloadReferenceId                             = nodesToDelete[ 0 ]?.data.payloadReferenceId,
              firstPayloadReference                               = global.data[ dataContext ]?.references[ firstPayloadReferenceId ],
              undelete                                            = mode === 'single' && firstPayloadReference?.isDeleted(),
              undeleteObjectType                                  = undelete && firstPayloadItem?.isDeleted() ? 'Item' : 'Reference';

        const doDeleteOrUndelete = () => {

            for ( const nodeToDelete of nodesToDelete ) {

                if ( nodeToDelete ) {
                    const
                        payloadId          = nodeToDelete?.data.payloadId,
                        payloadItem        = global.data[ dataContext ]?.items[ payloadId ],
                        payloadReferenceId = nodeToDelete?.data.payloadReferenceId,
                        payloadReference   = global.data[ dataContext ]?.references[ payloadReferenceId ];

                    if ( undelete ) {
                        // delete
                        if ( payloadReferenceId ) {
                            this.props.updateReference?.( dataContext, payloadReferenceId, { statusId: payloadReference.attributesModified.get( 'statusId' ) as number }, useElusiveDataFromDataContext ? undefined : [ uid ] );
                        }
                        if ( payloadId && undeleteObjectType === 'Item' ) {
                            this.props.updateItem?.( dataContext, payloadId, { statusId: payloadItem.attributesModified.get( 'statusId' ) as number }, useElusiveDataFromDataContext ? undefined : [ uid ] );
                        }
                    }
                    else {
                        // delete
                        if ( payloadReferenceId ) {
                            this.props.deleteReference?.(
                                dataContext, payloadReferenceId, true,
                                useElusiveDataFromDataContext ? undefined : [ uid ], false );
                        }
                        if ( payloadId && !deleteReferenceOnly ) {
                            this.props.deleteItem?.(
                                dataContext, payloadId, true,
                                useElusiveDataFromDataContext ? undefined : [ uid ], false, true );
                        }

                    }

                }

                if ( mode === 'single' ) {
                    const parentNode = this.treeUtils?.getNodeById( nodeToDelete?.data?.parentNodeId );
                    // select parentNode if exists
                    if ( parentNode ) {
                        this.props.onTreeSelectionChange?.( parentNode.key as string ?? '' );

                        this.props.setParams?.( uid, {
                            selectedKey               : parentNode.key as string ?? '',
                            selectedNodeId            : parentNode?.data?.itemId ?? 0,
                            selectedPayloadId         : parentNode?.data?.payloadId ?? 0,
                            selectedPayloadReferenceId: parentNode?.data?.payloadReferenceId ?? 0
                        } );
                    }
                    // select rootNode otherwise
                    else {
                        this.props.onTreeSelectionChange?.( '' );

                        this.props.setParams?.( uid, {
                            selectedKey               : '',
                            selectedNodeId            : 0,
                            selectedPayloadId         : 0,
                            selectedPayloadReferenceId: 0
                        } );
                    }
                }

                // TODO: change error handling to prevent duplicate errors ( _attributesForm )
                let formPanelKey = this.props.nodeIdToPanelKeyMap?.[ nodeId ?? this.state.contextMenuNode ?? '' ];
                formPanelKey = formPanelKey ? formPanelKey.replace( '_attributesForm', '' ) : undefined;
                if ( formPanelKey ) {
                    const formPanelKeyWithoutPrefix = formPanelKey.replace( this.props.parentUid + '_', '' );
                    const panelProps = { ...getPanelProps( this.props.parentUid ) };
                    if ( panelProps ) {
                        delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix ];
                        delete panelProps.panelProps.subPanelProps[ formPanelKeyWithoutPrefix + '_attributesForm' ];
                        setPanelProps( this.props.parentUid, panelProps );
                        // delete error of form
                        const subPanelErrors = { ...panelProps.panelProps.subPanelErrors };
                        delete subPanelErrors[ formPanelKey ];
                        delete subPanelErrors[ formPanelKey + '_attributesForm' ];
                        this.props.setParam?.( this.props.parentUid, 'subPanelErrors', subPanelErrors );
                        // delete dirty of node
                        if ( panelProps.panelProps.dirtyNodeIds?.includes( nodeId ?? this.state.contextMenuNode ?? '' ) ) {
                            const dirtyNodeIds = ( [] as string[] ).concat( panelProps.panelProps.dirtyNodeIds ?? [] );
                            dirtyNodeIds.splice( dirtyNodeIds.indexOf( nodeId ?? this.state.contextMenuNode ?? '' ), 1 );
                            this.props.setParam?.( this.props.parentUid, 'dirtyNodeIds', dirtyNodeIds );
                        }
                    }


                    // delete elusive data of form
                    this.props.clearElusiveData?.( formPanelKey );
                    this.props.clearElusiveData?.( formPanelKey + '_attributesForm' );
                }

            }
        };

        if ( noConfirmation ) {
            doDeleteOrUndelete();
        }
        else {
            confirmPopup( {
                target         : document.getElementById( nodeId ?? this.state.contextMenuNode ) ?? undefined,
                message        : __( `YN.base.itemTree.${undelete ? 'un' : ''}delete${undelete ? undeleteObjectType : deleteObjectType}.text`, { itemKey: firstPayloadItem.key, itemName: firstPayloadItem.name, itemDisplayAttributeValue: firstPayloadItem.displayAttributeValue } ),
                content        : <></>,
                icon           : 'pi pi-question-circle',
                className      : 'yn-itemtree-confirm-popup',
                acceptLabel    : __( 'YN.base.common.yes' ),
                acceptClassName: 'p-button-success p-button-outlined',
                rejectLabel    : __( 'YN.base.common.no' ),
                rejectClassName: 'p-button-secondary p-button-outlined mr-3',
                accept         : doDeleteOrUndelete
            } ).show();
        }

    }

    openNewPanel( event : React.KeyboardEvent<Element> | React.MouseEvent<HTMLElement, React.MouseEvent> | KeyboardEvent, nodeId? : string ) {
        const { dataContext, schema, status } = this.props,
              node                            = this.treeUtils?.getNodeById( nodeId ?? this.state.lastContextMenuNode ),
              payloadId                       = node?.data.payloadId;

        if ( payloadId ) {
            const item = global.data[ dataContext ]?.items[ payloadId ];
            const panelConfig = user && item ? user.getPanelPropsForItemType( schema, item.itemTypeId, undefined, undefined, !!event?.altKey )[ 0 ] : {};

            this.props.openPanel?.(
                {
                    panelType: 'FinderDetail',
                    ...panelConfig,
                    schema: schema,
                    status: status,
                    //
                    rootItemId: payloadId
                },
                undefined,
                event as KeyboardEvent
            );
        }
    }

    /**
     * Get tree context menu
     */
    getTreeContextMenu( nodeId : string, showOnly : false | 'add' | 'delete' = false ) : MenuItem[] {
        const retVal : MenuItem[]    = [],
              {
                  uid,
                  dataContext
              }                      = this.props,
              node                   = this.treeUtils?.getNodeById( nodeId ),
              isDynamicNode          = node?.cypherMatchNum !== undefined,
              itemId                 = this.treeUtils?.getPayloadIdForNode( nodeId ) ?? 0,
              item                   = global.data[ dataContext ]?.items[ itemId ],
              referenceId            = node?.data?.payloadReferenceId ?? 0,
              reference              = global.data[ dataContext ]?.references[ referenceId ],
              itemTypeName           = item?.itemType.name,
              allowedReferenceSetIds = this.treeUtils?.getAllowedCirsIdForNode( nodeId ),
              contextMenuConfig      = this.getAddItemDialogConfig(
                  itemTypeName, allowedReferenceSetIds, node
              ),
              nodeIsDeleted          = reference?.isDeleted() || item?.isDeleted(),
              allowAdd               = !contextMenuConfig.disableAddExistingItem || !contextMenuConfig.disableAddNewItem,
              allowDelete            = !contextMenuConfig.disableDeleteReference || !contextMenuConfig.disableDeleteItem
                  || !contextMenuConfig.disableDeleteDescendantReferences || !contextMenuConfig.disableDeleteDescendantItems
                  || itemId < 0;

        let buttonId : string,
            menuItemId : string;

        if ( showOnly === false ) {
            retVal.push( {
                id     : `${nodeId}_expandAll`,
                label  : __( 'YN.base.itemTree.expandAll.title' ),
                icon   : 'far fa-plus-square yn-info',
                command: function() {
                    this.expandOrCollapseBranch( true );
                }.bind( this )
            } );

            retVal.push( {
                id     : `${nodeId}_collapseAll`,
                label  : __( 'YN.base.itemTree.collapseAll.title' ),
                icon   : 'far fa-minus-square yn-info',
                command: function() {
                    this.expandOrCollapseBranch( false );
                }.bind( this )
            } );

            /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
            if ( retVal.length && ( this.treeUtils?.isStopped || node?.stopped ) ) {
                retVal.push( { separator: true } );
            }

            if ( this.treeUtils?.isStopped ) {
                retVal.push( {
                    id     : `${nodeId}_loadAll`,
                    label  : __( 'YN.base.itemTree.loadAll.title' ),
                    icon   : 'far fa-plus-square yn-info',
                    command: function() {
                        void this.loadAllStoppedNodes( node );
                    }.bind( this )
                } );
            }

            if ( node?.stopped ) {
                retVal.push( {
                    id     : `${nodeId}_resumeLoad`,
                    label  : __( 'YN.base.itemTree.resumeLoad.title' ),
                    icon   : 'far fa-plus-square',
                    command: function() {
                        this.props.setParams?.( uid, {
                            resumedNodes: { ...this.props.resumedNodes, [ node.id! ]: true },
                            expandedKeys: { ...this.props.expandedKeys, [ node.id! ]: true }
                        } );
                        this.resumeLoad( node );
                    }.bind( this )
                } );
            }

            if ( isDynamicNode ) {
                if ( retVal.length ) {
                    retVal.push( { separator: true } );
                }

                retVal.push( {
                    id     : `${nodeId}_openInNewTab`,
                    label  : __( 'YN.base.itemTree.item.openInNewTab.title' ),
                    icon   : 'pi pi-external-link yn-info',
                    command: ( e : MenuItemCommandEvent ) => this.openNewPanel( e.originalEvent as unknown as React.MouseEvent<HTMLElement, React.MouseEvent> )
                } );
            }
        }

        if ( showOnly !== 'delete' && ( allowAdd || contextMenuConfig.allowClone ) ) {
            buttonId = `${uid}_${nodeId}_addButton`;

            if ( retVal.length ) {
                retVal.push( { separator: true } );
            }

            if ( !contextMenuConfig.disableAddNewItem ) {
                menuItemId = `${nodeId}_addItem`;

                retVal.push( {
                    id     : menuItemId,
                    label  : __( 'YN.base.itemTree.item.add.title' ),
                    icon   : 'pi pi-plus-circle yn-add',
                    data   : {
                        buttonId
                    },
                    command: ( e ) => this.nodeButtons[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: false, ctrlKey: false, shiftKey: false } ) as unknown as React.MouseEvent )
                } );
            }

            if ( !contextMenuConfig.disableAddExistingItem ) {
                menuItemId = `${nodeId}_addExistingItem`;

                retVal.push( {
                    id     : menuItemId,
                    label  : __( 'YN.base.itemTree.item.addExisting.title' ),
                    icon   : 'pi pi-plus-circle yn-add',
                    data   : {
                        buttonId
                    },
                    command: ( e ) => this.nodeButtons[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: true, ctrlKey: false, shiftKey: false } ) as unknown as React.MouseEvent )
                } );
            }

            if ( contextMenuConfig.allowClone ) {
                menuItemId = `${nodeId}_cloneItem`;

                retVal.push( {
                    id     : menuItemId,
                    label  : __( 'YN.base.itemTree.item.clone.title' ),
                    icon   : 'pi pi-clone yn-add',
                    data   : {
                        buttonId
                    },
                    command: ( e ) => this.nodeButtons[ e.item.data.buttonId! ].content?.expand( new MouseEvent( 'click', { altKey: true, ctrlKey: true, shiftKey: true } ) as unknown as React.MouseEvent )
                } );
            }
        }

        if ( showOnly !== 'add' && allowDelete ) {
            let nodesToDelete : ExtendTreeNode[],
                firstPayloadId : number,
                firstPayloadItem : Item,
                firstPayloadReferenceId : number,
                firstPayloadReference : Reference,
                undelete : boolean,
                undeleteObjectType : string;

            buttonId = `${uid}_${nodeId}_deleteButton`;

            if ( retVal.length ) {
                retVal.push( { separator: true } );
            }

            // undelete option
            if ( nodeIsDeleted ) {
                menuItemId = `${nodeId}_undeleteItem`;

                retVal.push( {
                    id     : menuItemId,
                    label  : __( 'YN.base.itemTree.item.undeleteItem.title' ),
                    icon   : 'pi pi-plus-circle yn-dirty',
                    data   : {
                        buttonId,
                        confirmTitle: __( `YN.base.itemTree.undeleteItem.text` )
                    },
                    command: ( e : MenuItemCommandEvent ) => {
                        if ( !( e.originalEvent as React.SyntheticEvent & KeyboardEvent ).altKey ) {
                            this.nodeButtons[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent );
                        }
                        else {
                            this.deleteOrUndeleteNode( !contextMenuConfig.disableDeleteReference, nodeId, 'single', true );
                        }
                    }
                } );
            }

            // delete options
            else {
                if ( !contextMenuConfig.disableDeleteItem || itemId < 0 || !contextMenuConfig.disableDeleteReference ) {
                    nodesToDelete = node ? [ node ] : [];

                    if ( nodesToDelete.length ) {
                        firstPayloadId = nodesToDelete[ 0 ]?.data.payloadId;
                        firstPayloadItem = global.data[ dataContext ]?.items[ firstPayloadId ];
                        firstPayloadReferenceId = nodesToDelete[ 0 ]?.data.payloadReferenceId;
                        firstPayloadReference = global.data[ dataContext ]?.references[ firstPayloadReferenceId ];
                        undelete = firstPayloadReference?.isDeleted();
                        undeleteObjectType = undelete && firstPayloadItem?.isDeleted() ? 'Item' : 'Reference';

                        if ( firstPayloadItem ) {
                            if ( !contextMenuConfig.disableDeleteItem || itemId < 0 ) {
                                menuItemId = `${nodeId}_deleteItem`;

                                retVal.push( {
                                    id     : menuItemId,
                                    label  : __( 'YN.base.itemTree.item.deleteItem.title' ),
                                    icon   : 'pi pi-minus-circle yn-delete',
                                    data   : {
                                        buttonId,
                                        confirmTitle: __( `YN.base.itemTree.${undelete ? 'un' : ''}delete${undelete ? undeleteObjectType : 'Item'}.text`, { itemKey: firstPayloadItem.key, itemName: firstPayloadItem.name, itemDisplayAttributeValue: firstPayloadItem.displayAttributeValue } )
                                    },
                                    command: ( e : MenuItemCommandEvent ) => {
                                        if ( !( e.originalEvent as React.SyntheticEvent & KeyboardEvent ).altKey ) {
                                            this.nodeButtons[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent );

                                            if ( this.nodeButtonPanels[ e.item.id! ] ) {
                                                setTimeout(
                                                    () => {
                                                        this.nodeButtonPanels[ e.item.id! ].expand( e.originalEvent as unknown as React.MouseEvent );
                                                    },
                                                    12
                                                );
                                            }
                                        }
                                        else {
                                            this.deleteOrUndeleteNode( false, nodeId, 'single', true );
                                        }
                                    }
                                } );
                            }

                            if ( !contextMenuConfig.disableDeleteReference ) {
                                menuItemId = `${nodeId}_deleteReference`;

                                retVal.push( {
                                    id     : menuItemId,
                                    label  : __( 'YN.base.itemTree.item.deleteReference.title' ),
                                    icon   : 'pi pi-minus-circle yn-delete',
                                    data   : {
                                        buttonId,
                                        confirmTitle: __( `YN.base.itemTree.${undelete ? 'un' : ''}delete${undelete ? undeleteObjectType : 'Reference'}.text`, { itemKey: firstPayloadItem.key, itemName: firstPayloadItem.name, itemDisplayAttributeValue: firstPayloadItem.displayAttributeValue } )
                                    },
                                    command: ( e : MenuItemCommandEvent ) => {
                                        if ( !( e.originalEvent as React.SyntheticEvent & KeyboardEvent ).altKey ) {
                                            this.nodeButtons[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent );

                                            if ( this.nodeButtonPanels[ e.item.id! ] ) {
                                                setTimeout(
                                                    () => {
                                                        this.nodeButtonPanels[ e.item.id! ].expand( e.originalEvent as unknown as React.MouseEvent );
                                                    },
                                                    12
                                                );
                                            }
                                        }
                                        else {
                                            this.deleteOrUndeleteNode( true, nodeId, 'single', true );
                                        }
                                    }
                                } );
                            }
                        }
                    }
                }

                if ( !contextMenuConfig.disableDeleteDescendantItems || itemId < 0 || !contextMenuConfig.disableDeleteDescendantReferences ) {
                    nodesToDelete = this.treeUtils?.getChildNodesById( nodeId ) ?? [];

                    if ( nodesToDelete.length ) {
                        firstPayloadId = nodesToDelete[ 0 ]?.data.payloadId;
                        firstPayloadItem = global.data[ dataContext ]?.items[ firstPayloadId ];

                        if ( firstPayloadItem ) {
                            if ( !contextMenuConfig.disableDeleteDescendantItems || itemId < 0 ) {
                                menuItemId = `${nodeId}_descendantReferences`;

                                retVal.push( {
                                    id     : menuItemId,
                                    label  : __( 'YN.base.itemTree.item.deleteDescendantItems.title' ),
                                    icon   : 'pi pi-minus-circle yn-delete',
                                    data   : {
                                        buttonId,
                                        confirmTitle: __( `YN.base.itemTree.deleteDescendantItems.text`, { itemKey: firstPayloadItem.key, itemName: firstPayloadItem.name, itemDisplayAttributeValue: firstPayloadItem.displayAttributeValue } )
                                    },
                                    command: ( e : MenuItemCommandEvent ) => {
                                        if ( !( e.originalEvent as React.SyntheticEvent & KeyboardEvent ).altKey ) {
                                            this.nodeButtons[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent );

                                            if ( this.nodeButtonPanels[ e.item.id! ] ) {
                                                setTimeout(
                                                    () => {
                                                        this.nodeButtonPanels[ e.item.id! ].expand( e.originalEvent as unknown as React.MouseEvent );
                                                    },
                                                    12
                                                );
                                            }
                                        }
                                        else {
                                            this.deleteOrUndeleteNode( false, nodeId, 'ancestor', true );
                                        }
                                    }
                                } );
                            }

                            if ( !contextMenuConfig.disableDeleteDescendantReferences ) {
                                menuItemId = `${nodeId}_descendantReferences`;

                                retVal.push( {
                                    id     : menuItemId,
                                    label  : __( 'YN.base.itemTree.item.deleteDescendantReferences.title' ),
                                    icon   : 'pi pi-minus-circle yn-delete',
                                    data   : {
                                        buttonId,
                                        confirmTitle: __( `YN.base.itemTree.deleteDescendantReferences.text`, { itemKey: firstPayloadItem.key, itemName: firstPayloadItem.name, itemDisplayAttributeValue: firstPayloadItem.displayAttributeValue } )
                                    },
                                    command: ( e : MenuItemCommandEvent ) => {
                                        if ( !( e.originalEvent as React.SyntheticEvent & KeyboardEvent ).altKey ) {
                                            this.nodeButtons[ e.item.data.buttonId! ].content?.expand( e.originalEvent as unknown as React.MouseEvent );

                                            if ( this.nodeButtonPanels[ e.item.id! ] ) {
                                                setTimeout(
                                                    () => {
                                                        this.nodeButtonPanels[ e.item.id! ].expand( e.originalEvent as unknown as React.MouseEvent );
                                                    },
                                                    12
                                                );
                                            }
                                        }
                                        else {
                                            this.deleteOrUndeleteNode( true, nodeId, 'ancestor', true );
                                        }
                                    }
                                } );
                            }
                        }
                    }
                }
            }
        }

        return retVal;
    }

    /**
     * Resume load of tree data after stopLoafing
     * @param node the node at that the resume starts
     * @param successCallback
     */
    async resumeLoad( node : ExtendTreeNode, successCallback? : ( responseData : any ) => void ) {
        const {
                  dataContext,
                  schema,
                  uid,
                  useElusiveDataFromDataContext
              } = this.props;

        const payloadId = node?.data?.payloadId ?? node?.data?.delegatedPayloadId;
        const dataObjectKey = this.treeUtils?.getDataObjectKeyForPayloadId( payloadId );
        if ( dataObjectKey ) {
            await this.props.loadData(
                'tree',
                dataContext,
                useElusiveDataFromDataContext
                    ? dataContext
                    : uid,
                undefined,
                schema,
                {
                    mergeMode: 'keepExisting' as MergeMode,
                    params   : {
                        dataObjectKey,
                        rootItemId: payloadId,
                        schema
                    }
                },
                undefined,
                successCallback
            );
        }
    }

    getAddItemDialogConfig( targetItemTypeName? : string, allowedReferenceSetIds? : {
        [ cirsId : number ] : string
    }, node? : ExtendTreeNode ) : ContextMenuConfig {
        const { schema, uid, dataContext, useElusiveDataFromDataContext } = this.props,
              nodeItem                                                    = node?.data?.ciItem,
              isDynamicNode                                               = node?.cypherMatchNum !== undefined;

        let retVal : ContextMenuConfig = {} as ContextMenuConfig;
        let referenceDirection : ( 'src' | 'dest' ) = 'src';

        // check if relevant properties has been changed

        // check if targetItemTyp is valid for allowed reference sets
        const targetItemTypeId = metaModel[ schema ].getItemType( targetItemTypeName ?? '' )?.id ?? 0;

        // allow only reference sets that have the targetItemType as destination
        const includeReferenceSets = Object.keys( allowedReferenceSetIds ?? {} ).filter( cirsId => {
            const srcOrDestItemTypeId = allowedReferenceSetIds![ cirsId ]! === 'src' ? 'srcItemTypeId' : 'destItemTypeId';
            const include = metaModel[ schema ].getReferenceSet( cirsId * 1 )?.[ srcOrDestItemTypeId ] === targetItemTypeId;
            if ( include ) {
                referenceDirection = allowedReferenceSetIds![ cirsId ]! as ( 'src' | 'dest' ) === 'src' ? 'dest' : 'src';
            }
            return include;
        } ).map( cirsId => cirsId * 1 );

        retVal = {
            ...retVal,
            includeReferenceSets,
            // get disableDelete* directly from appropriate node
            ...( isDynamicNode && nodeItem ? _.pick( nodeItem, [ 'disableDeleteItem', 'disableDeleteReference' ] ) : undefined ),
            ...( nodeItem ? _.pick( nodeItem, [ 'disableDeleteDescendantItems', 'disableDeleteDescendantReferences' ] ) : undefined ),
            // get disableAdd* from child nodes
            ...( node ? this.treeUtils?.getAddPermissions( node ) : undefined )
        };

        if ( !isDynamicNode ) {
            retVal.disableDeleteItem = true;
            retVal.disableDeleteReference = true;
            retVal.disableDeleteDescendantItems = true;
            retVal.disableDeleteDescendantReferences = true;
        }
        else if ( node?.data?.ciItem ) {
            const nodeItemTypeId = global.data[ ( useElusiveDataFromDataContext ? dataContext : uid ) + '-meta' ]?.items[ node.data.ciItem.id ]?.itemTypeId;
            if ( nodeItemTypeId ) {
                if ( isDynamicNode ) {
                    retVal.disableDeleteItem = retVal.disableDeleteItem ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteItem' )?.valueDefault;
                    retVal.disableDeleteReference = retVal.disableDeleteReference ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteReference' )?.valueDefault;
                }
                retVal.disableDeleteDescendantItems = retVal.disableDeleteDescendantItems ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteDescendantItems' )?.valueDefault;
                retVal.disableDeleteDescendantReferences = retVal.disableDeleteDescendantReferences ?? metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( nodeItemTypeId, 'DisableDeleteDescendantReferences' )?.valueDefault;
            }
        }

        // TODO: dynamic handling of referenceDirection like in AddTreeNodeDialog
        const possibleItemTypes = _.uniq( includeReferenceSets ).map( cirsId => metaModel[ schema ].getReferenceSet( cirsId )?.[ referenceDirection === 'src' ? 'srcItemTypeId' : 'destItemTypeId' ] ?? 0 )
            .map( citId => metaModel[ schema ].getItemType( citId )?.name ?? '' );

        if ( !possibleItemTypes.length ) {
            retVal.disableAddExistingItem = true;
            retVal.disableAddNewItem = true;
        }

        // TODO: check cardinality
        retVal.allowClone = !retVal.disableAddNewItem;

        // check for conditional permissions
        if ( node ) {
            retVal = { ...retVal, ...this.treeUtils?.getConditionalNodePermissions( node, retVal as NodePermissions ) };
        }

        return retVal;
    }

    expandOrCollapseBranch( expand : boolean ) {
        const startNode = this.treeUtils?.getNodeById( this.state.lastContextMenuNode );

        // todo: TreeTableExpandedKeysType not defined in our PrimeReact version => check after update again
        const expandedKeys : { [ key : string ] : boolean; } = { ...this.props.expandedKeys };
        const expandNode = ( node : ExtendTreeNode ) => {
            if ( node!.children?.length ) {
                expandedKeys[ node!.key! ] = expand;
                for ( const child of node.children ) {
                    expandNode( child );
                }
            }
        }

        if ( startNode ) {
            expandNode( startNode );
        }

        this.props.setParam?.( this.props.uid, 'expandedKeys', expandedKeys );

    }

    async loadAllStoppedNodes( startNode : ExtendTreeNode, expandedOnly : boolean = false ) {
        const resumedNodes : { [ key : string ] : boolean; } = { ...this.props.resumedNodes },
              { expandedKeys }                               = this.props;
        const loadNode = async ( node : ExtendTreeNode ) => {
            // TODO: !node.children?.length doesn't prevent multiple loading, check why and fix
            if ( node!.stopped && !resumedNodes[ node.key! ] && ( !expandedOnly || expandedKeys[ node.key! ] ) && ( !node.children?.length || expandedOnly ) ) {
                await this.resumeLoad( node );
                resumedNodes[ node.key! ] = true;
            }
            if ( node.children ) {
                for ( const child of node.children ) {
                    await loadNode( child );
                }
            }
        }

        await new Promise( resolve => {
            this.setState( { deferRender: true }, () => resolve( true ) );
        } );

        await loadNode( startNode );

        this.props.setParam?.( this.props.uid, 'resumedNodes', resumedNodes );

        this.setState( { deferRender: false } );
    }

    getAddNodeButton( nodeId : string ) {
        const {
                  uid
              }        = this.props,
              buttonId = `${uid}_${nodeId}_addButton`;

        this.nodeButtons[ buttonId ] = {
            justInteracted: false
        };

        return <ContentButton
            key={buttonId}
            id={buttonId}

            ref={node => {
                if ( node ) {
                    this.nodeButtons[ buttonId ] = {
                        ...this.nodeButtons[ buttonId ],
                        content: node
                    };
                }
            }}

            icon="pi pi-plus-circle yn-add"
            rounded={true}
            text={true}
            horizontalContentAlign="left"
            verticalContentAlign="bottom"

            onExpand={( event ) => {
                let retVal : boolean                    = false,
                    mode : 'new' | 'existing' | 'clone' = 'new',
                    process                             = false;

                if ( event.shiftKey && event.ctrlKey && event.altKey ) {
                    mode = 'clone'
                }
                else if ( event.altKey ) {
                    mode = 'existing';
                }

                const stateToUse = mode === 'clone'
                          ? this.addNode( 'clone', this.treeUtils?.getNodeById( nodeId )?.data?.parentNodeId ?? 0, nodeId )
                          : this.addNode( mode, nodeId, undefined ),
                      {
                          dataContext,
                          schema
                      }          = this.props,
                      {
                          addItemDialogConfig
                      }          = stateToUse;

                if ( mode === 'new' ) {
                    if ( !addItemDialogConfig.disableAddNewItem ) {
                        process = true;
                    }
                    else if ( !addItemDialogConfig.disableAddExistingItem ) {
                        mode = 'existing';
                        this.addNode( mode, nodeId, undefined );
                        process = true;
                    }
                }
                else if ( mode === 'existing' ) {

                    if ( !addItemDialogConfig.disableAddExistingItem ) {
                        process = true;
                    }
                    else if ( !addItemDialogConfig.disableAddNewItem ) {
                        mode = 'new';
                        this.addNode( mode, nodeId, undefined );
                        process = true;
                    }
                }
                else if ( mode === 'clone' && addItemDialogConfig.allowClone ) {
                    process = true;
                }

                if ( process ) {
                    this.addButtonContent[ nodeId ] = getAddItemDialog( {
                        uid                   : uid + '-AddItemReferenceDialog_button_content',
                        dataContext,
                        schema                : schema!,
                        addMode               : mode,
                        rawMode               : false,
                        showAdditionalRawNames: false,
                        onAddNode             : this.onAddNewNode,
                        onHide                : () => {
                            this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                        },
                        onInteraction         : () => {
                            this.nodeButtons[ buttonId ].justInteracted = true;
                            this.ignoreSelectionChange = true;
                        },
                        ...addItemDialogConfig
                    } );
                }
                else {
                    delete this.addButtonContent[ nodeId ];
                }

                if ( this.addButtonContent[ nodeId ] ) {
                    retVal = true;
                }

                event.stopPropagation();

                return retVal;
            }}
            onCollapse={( event, isOutsideClicked ) => {
                const innerRetVal = !this.nodeButtons[ buttonId ].justInteracted && isOutsideClicked;

                this.nodeButtons[ buttonId ].justInteracted = false;

                if ( !innerRetVal ) {
                    this.ignoreSelectionChange = true;
                }
                else {
                    this.ignoreSelectionChange = false;
                }

                return innerRetVal;
            }}
            content={() => {
                return this.addButtonContent[ nodeId ];
            }}
        />;
    }

    getDeleteNodeButton( nodeId : string ) {
        const {
                  uid
              }                = this.props,
              buttonId         = `${uid}_${nodeId}_deleteButton`,
              contentMenuItems = this.getTreeContextMenu( nodeId, 'delete' );

        let retVal : React.ReactElement;

        this.nodeButtons[ buttonId ] = {
            justInteracted: false
        };

        if ( contentMenuItems.length === 1 ) {
            const key = contentMenuItems[ 0 ].id!;

            retVal = <ContentButton
                key={buttonId}
                id={buttonId}

                ref={node => {
                    if ( node ) {
                        this.nodeButtons[ buttonId ] = {
                            ...this.nodeButtons[ buttonId ],
                            content: node
                        };
                    }
                }}

                icon={contentMenuItems[ 0 ].icon}
                rounded={true}
                text={true}
                horizontalContentAlign="left"
                verticalContentAlign="bottom"

                onExpand={( event ) => {
                    let innerRetVal : boolean = true;

                    if ( event.altKey || !contentMenuItems[ 0 ].data.confirmTitle ) {
                        contentMenuItems[ 0 ].command?.( {
                            originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                            item         : contentMenuItems[ 0 ]
                        } );

                        innerRetVal = false;
                    }

                    return innerRetVal;
                }}

                content={contentMenuItems[ 0 ].data.confirmTitle
                    ? <Panel
                        key={key}
                        id={`${uid}_${key}_panel`}

                        style={{ width: '25rem' }}
                        className="yn-confirm-option-panel p-0"
                        pt={{
                            content: {
                                style: {
                                    border: 'none'
                                }
                            }
                        }}
                    >
                        <label>
                            <span className="pi pi-question-circle yn-blue"></span>
                            {contentMenuItems[ 0 ].data.confirmTitle}
                        </label>
                        <Button
                            label={__( 'YN.base.common.no' )}
                            className="p-button-sm p-button-secondary mr-3"
                            rounded={false}
                            text={false}
                            outlined={true}
                            onClick={( event ) => {
                                this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                            }}
                        />
                        <Button
                            label={__( 'YN.base.common.yes' )}
                            className="p-button-sm p-confirm-popup-accept"
                            rounded={false}
                            text={false}
                            outlined={true}
                            onClick={( event ) => {
                                contentMenuItems[ 0 ].command?.( {
                                    originalEvent: new MouseEvent( 'click', { altKey: true } ) as unknown as React.MouseEvent,
                                    item         : contentMenuItems[ 0 ]
                                } );

                                this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                            }}
                        />
                    </Panel>
                    : null}
            />;
        }
        else {
            const buttonContent : React.ReactNode[] = [];

            for ( let i = 0; i < contentMenuItems.length; i++ ) {
                const key = contentMenuItems[ i ].id!;

                delete this.nodeButtonPanels[ key ];

                buttonContent.push(
                    <Panel
                        key={key}
                        id={`${uid}_${key}_panel`}

                        ref={node => {
                            if ( node ) {
                                this.nodeButtonPanels[ key ] = node;
                            }
                        }}

                        header={
                            <Button
                                label={contentMenuItems[ i ].label}
                                icon={contentMenuItems[ i ].icon}
                                rounded={false}
                                text={true}
                                className="yn-menu-button yn-no-rounded-borders"
                                onClick={( event ) => {
                                    if ( event.altKey || !contentMenuItems[ i ].data.confirmTitle ) {
                                        contentMenuItems[ i ].command?.( {
                                            originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                                            item         : contentMenuItems[ i ]
                                        } );

                                        this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                    }
                                    else {
                                        for ( const dPanelId of Object.keys( this.nodeButtonPanels ) ) {
                                            if ( dPanelId !== key ) {
                                                this.nodeButtonPanels[ dPanelId ].collapse( event );
                                            }
                                            else {
                                                this.nodeButtonPanels[ dPanelId ].toggle( event );
                                            }
                                        }
                                    }
                                }}
                            />
                        }

                        toggleable={true}
                        collapsed={true}
                        className="yn-confirm-option-panel"
                        pt={{
                            root   : {
                                style: {
                                    padding: '0'
                                }
                            },
                            content: {
                                className: 'yn-confirm-option-panel-content'
                            }
                        }}
                    >
                        {contentMenuItems[ i ].data.confirmTitle &&
                            <>
                                <label>
                                    <span className="pi pi-question-circle"></span>
                                    {contentMenuItems[ i ].data.confirmTitle}
                                </label>
                                <Button
                                    label={__( 'YN.base.common.no' )}
                                    className="p-button-sm p-button-secondary mr-3"
                                    rounded={false}
                                    text={false}
                                    outlined={true}
                                    onClick={( event ) => {
                                        this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                    }}
                                />
                                <Button
                                    label={__( 'YN.base.common.yes' )}
                                    className="p-button-sm p-confirm-popup-accept"
                                    rounded={false}
                                    text={false}
                                    outlined={true}
                                    onClick={( event ) => {
                                        contentMenuItems[ i ].command?.( {
                                            originalEvent: new MouseEvent( 'click', { altKey: true } ) as unknown as React.MouseEvent,
                                            item         : contentMenuItems[ i ]
                                        } );

                                        this.nodeButtons[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                    }}
                                />
                            </>
                        }
                    </Panel>
                );
            }

            retVal = <ContentButton
                key={buttonId}
                id={buttonId}

                ref={node => {
                    if ( node ) {
                        this.nodeButtons[ buttonId ] = {
                            ...this.nodeButtons[ buttonId ],
                            content: node
                        };
                    }
                }}

                icon="pi pi-minus-circle yn-delete"
                rounded={true}
                text={true}
                horizontalContentAlign="left"
                verticalContentAlign="bottom"

                content={
                    <Panel
                        style={{ width: '25rem' }}
                        className="yn-confirm-option-panel"
                        pt={{
                            content: {
                                style: {
                                    padding: '.5rem 1rem'
                                }
                            }
                        }}
                    >
                        {buttonContent}
                    </Panel>
                }
            />;
        }

        return retVal;
    }

    /**
     * Event called when moving a drag node over a possible drop target.
     *
     * @param {TreeNode} dragNode the dragged tree node
     * @param {TreeNode} dropNode the drop (target) node
     * @return {boolean} return false if dropping is not allowed on the currently hovered node
     */
    onDragStart( event : TreeDragStartParams ) {
        /*const dragGhostContent = document.getElementById( event.dragNode.id as string )?.cloneNode( true );
        const dragGhost = document.createElement( 'div' );
        if ( dragGhost && dragGhostContent ) {
            dragGhost.id = "drag-ghost";

            if ( event.originalEvent.altKey ) {
                dragGhost.className = 'yn-drag-copy'
            }
            dragGhost.style.position = "absolute";
            dragGhost.style.display = "flex";
            dragGhost.style.top = "-1000px";
            dragGhost.appendChild( dragGhostContent );

            const copyIcon = document.createElement( 'span' );
            copyIcon.className = 'p-badge p-badge-success';
            copyIcon.textContent = '+';
            dragGhost.appendChild( copyIcon );

            document.body.appendChild( dragGhost );
            event.originalEvent.dataTransfer.setDragImage(dragGhost, 0, 0);
        }*/
    }

    /**
     * Event called when moving a drag node over a possible drop target.
     *
     * @param {TreeNode} dragNode the dragged tree node
     * @param {TreeNode} dropNode the drop (target) node
     * @return {boolean} return false if dropping is not allowed on the currently hovered node
     */
    onDragEnd( event : TreeDragEndParams ) {
        /*const dragGhost = document.getElementById( 'drag-ghost' );
        if ( dragGhost?.parentNode ) {
            dragGhost?.parentNode.removeChild( dragGhost );
        }*/
    }

    /**
     * Handle dragOver and set the appropriate dropEffect (move/copy/link) by key modifiers
     * @param event
     */
    onDragOver( event : DragEvent ) {
        if ( event.dataTransfer ) {
            event.dataTransfer.dropEffect = keyboardUtils.getDropEffect( event );
        }
    }

    /**
     * Event called when moving a drag node over a possible drop target.
     *
     * @param {TreeNode} dragNode the dragged tree node
     * @param {TreeNode} dropNode the drop (target) node
     * @return {boolean} return false if dropping is not allowed on the currently hovered node
     */
    onValidateDrop( dragNode : ExtendTreeNode, dropNode : ExtendTreeNode, originalEvent : DragEvent ) {
        let retVal = false;

        if ( dragNode && dropNode && this.treeUtils ) {
            const dropEffect = keyboardUtils.getDropEffect( originalEvent );
            retVal = !!this.treeUtils.checkDropTarget( dragNode, dropNode, dropEffect );
        }

        return retVal;
    }

    /**
     * Drop handler. Moved tree node, removes old and sets new reference
     *
     * @param e The drop event
     */
    onDrop( e : TreeDragDropEvent ) {
        const { dragNode, dropNode } = e as any as { dragNode : ExtendTreeNode, dropNode : ExtendTreeNode };
        const { uid, dataContext, schema, useElusiveDataFromDataContext } = this.props;
        // TODO: move this business logic out of component?!
        if ( dragNode && dropNode && this.treeUtils ) {
            const dropEffect = keyboardUtils.getDropEffect( e.originalEvent );
            const refConfig : ReferenceDescription[] | false = this.treeUtils.checkDropTarget( dragNode, dropNode, dropEffect );
            // delete old reference and add new one
            if ( refConfig !== false && refConfig.length ) {
                let chosenReferenceIndex = 0;
                const doDrop = async () => {
                    // delete old reference, if moving (not linking or copying)
                    if ( !dragNode.keepReferenceOnDrop && dropEffect === 'move' ) {
                        this.props.deleteReference?.( dataContext, dragNode.data.payloadReferenceId, true, useElusiveDataFromDataContext ? undefined : [ uid ] );
                    }
                    // do a real copy (clone)
                    if ( dropEffect === 'copy' ) {
                        const clonedItem = new Item( { itemTypeId: global.data[ dataContext ]?.items[ e.dragNode.data.payloadId ]?.itemTypeId }, dataContext, schema ).update( global.data[ dataContext ]?.items[ e.dragNode.data.payloadId ]?.toUpdateJSON() );
                        if ( clonedItem ) {
                            this.props.addItem?.( dataContext, clonedItem, schema, useElusiveDataFromDataContext ? undefined : [ uid ] );
                            const refConfigx : ReferenceDescription = refConfig[ chosenReferenceIndex ];
                            refConfigx[ refConfigx!.direction === 'SrcToDest' ? 'srcItemId' : 'destItemId' ] = Item.uniqueIdCount + 1;
                            this.props.addReference?.( dataContext, refConfigx as ReferenceAttributesForCreation, schema!, useElusiveDataFromDataContext ? undefined : [ uid ] );
                        }
                    }
                    // move or link
                    else {
                        this.props.addReference?.( dataContext, refConfig[ chosenReferenceIndex ] as ReferenceAttributesForCreation, schema!, useElusiveDataFromDataContext ? undefined : [ uid ] );
                    }
                    const refId = Reference.uniqueIdCount + 1;
                    this.treeUtils?.updateDroppedNode( dragNode, dropNode, refId );

                    // fire action on drop
                    await this.onTriggerAction( dragNode.id, dropNode.id, e );

                };

                if ( refConfig.length > 1 ) {
                    const Message = () => {
                        const [ refIdx, setRefIdx ] = useState( chosenReferenceIndex );
                        return (
                            <Dropdown
                                options={refConfig}
                                optionValue="idx"
                                optionLabel="referenceTypeName"
                                value={refIdx}
                                onChange={( dde : DropdownChangeEvent ) => {
                                    setRefIdx( dde.value );
                                    chosenReferenceIndex = dde.value;
                                }}
                            />
                        );
                    };
                    confirmPopup( {
                        target         : document.getElementById( dropNode.id ) ?? undefined,
                        message        : <><Message/></>,
                        content        : <></>,
                        icon           : 'pi pi-question-circle',
                        className      : 'yn-itemtree-confirm-popup',
                        acceptLabel    : __( 'YN.base.common.ok' ),
                        acceptClassName: 'p-button-success p-button-outlined',
                        rejectLabel    : __( 'YN.base.common.cancel' ),
                        rejectClassName: 'p-button-secondary p-button-outlined mr-3',
                        accept         : () => void doDrop()
                    } ).show();
                }
                else {
                    void doDrop();
                }


            }
        }
    }

    /**
     * Handles selection of tree node. Basically sets ciId in components state to display the appropriate form beside the tree.
     *
     * @param event Event fired by PrimeReact Tree
     */
    onSelectTreeNode( event : TreeSelectionEvent ) {
        if ( !this.ignoreSelectionChange ) {
            const node        = this.treeUtils?.getNodeById( String( event.value ) ),
                  payloadItem = global.data[ this.props.dataContext ].items[ node?.data.payloadId ];

            // deleted nodes could not be selected
            if ( !payloadItem || payloadItem.statusId !== -1 ) {
                this.props.onTreeSelectionChange?.( event.value as string );

                this.props.setParams?.( this.props.uid, {
                    lastSelectionParameters   : {
                        selectedKey               : this.props.selectedKey,
                        selectedNodeId            : this.props.selectedNodeId,
                        selectedPayloadId         : this.props.selectedPayloadId,
                        selectedPayloadReferenceId: this.props.selectedPayloadReferenceId
                    },
                    selectedKey               : event.value,
                    selectedNodeId            : node?.data?.itemId ?? 0,
                    selectedPayloadId         : node?.data?.payloadId ?? 0,
                    selectedPayloadReferenceId: node?.data?.payloadReferenceId ?? 0
                } );

                const parentNode = this.treeUtils?.getNodeById( node?.data.parentNodeId );
                const parentItemId = parentNode?.data?.payloadId ?? parentNode?.data?.itemId ?? 0;
                this.props.onSelectItem?.( event.value as string, node?.data?.itemId, node?.data?.payloadId, event, node?.data.parentNodeId ?? '', parentItemId, this.props.uid );
            }
        }

        this.ignoreSelectionChange = false;
    }

    /**
     * Handles expanding/collapsing of tree node.
     *
     * @param event Event fired by PrimeReact Tree
     */
    onToggleTreeNode( event : TreeExpandedEvent ) {
        const { expandedKeys = {} } = this.props,
              nextExpandedKeys      = event.value;

        // this is necessary as PrimeReact removes collapsed nodes from list, and we need to detect what's different from initial loaded state
        for ( const key of Object.keys( expandedKeys ) ) {
            nextExpandedKeys[ key ] = nextExpandedKeys[ key ] ?? false;
        }

        this.props.setParam?.( this.props.uid, 'expandedKeys', nextExpandedKeys );
        this.props.onTreeToggleNode?.( nextExpandedKeys );
    }

    onAddNewNode( item : ItemAttributesForCreation | Item, reference : ReferenceAttributesForCreation ) {
        const {
                  uid,
                  dataContext,
                  schema,
                  useElusiveDataFromDataContext
              } = this.props;

        if ( item instanceof Item && !item.isNew() ) {
            // item must be re-generated as SearchItemDropdown doesn't register its results
            item = new Item( item.toJSON(), dataContext, schema );
        }

        const newItem = this.props.addItem?.(
            dataContext,
            item,
            schema!,
            useElusiveDataFromDataContext
                ? undefined
                : [ uid ]
        );

        reference.srcItemId = reference.srcItemId || newItem.id;
        reference.destItemId = reference.destItemId || newItem.id;

        const newReference = this.props.addReference?.(
            dataContext,
            reference,
            schema!,
            useElusiveDataFromDataContext
                ? undefined
                : [ uid ]
        );

        this.props.onTreeSelectionChange?.(
            this.treeUtils?.getDynamicNodeIdByPayloadReferenceId( newReference.id )?.[ 0 ]
            ?? this.treeUtils?.getDynamicNodeIdByPayloadId( newItem.id )?.[ 0 ]
            ?? ''
        );

        // set selectedPayloadId to newly added item and panelKeys to an empty string
        this.props.setParams?.( uid, {
            lastSelectionParameters   : {
                selectedKey               : this.props.selectedKey,
                selectedNodeId            : this.props.selectedNodeId,
                selectedPayloadId         : this.props.selectedPayloadId,
                selectedPayloadReferenceId: this.props.selectedPayloadReferenceId
            },
            selectedPayloadId         : newItem.id,
            selectedPayloadReferenceId: newReference.id,
            selectedKey               : '',
            selectedNodeId            : 0
        } );
    }

    onNodeDoubleClick( event : TreeNodeDoubleClickEvent ) {
        if ( ( event.node as ExtendTreeNode | undefined )?.cypherMatchNum !== undefined ) {
            this.openNewPanel( event.originalEvent as unknown as React.MouseEvent<HTMLElement, React.MouseEvent>, event.node.id );
        }
    }

    /**
     * Template for tree nodes.
     *
     * @private
     */
    nodeTemplate( node : TreeNode.TreeNode, options : TreeNodeTemplateOptions, items? : ItemCollection, filterValue? : string ) : React.ReactNode[] {
        const retVal : React.ReactNode[] = [],
              {
                  dirtyNodeIds,
                  elusiveData,
                  dataContext
              }                          = this.props,
              {
                  nodeIdToErrorDataMap,
                  runsInDesignerContext
              }                          = this.state,
              rootItemId                 = elusiveData?.data?.rootItemId,
              nodeLabel                  = node.label?.replaceAll( /[\u200B-\u200E\uFEFF]/g, '' ) ?? '',
              payloadReferenceId         = node.data.payloadReferenceId,
              payloadReference           = global.data[ dataContext ]?.references[ payloadReferenceId ],
              currentItemId              = this.treeUtils?.getPayloadIdForNode( node.id! ) ?? 0,
              currentItem                = items?.[ currentItemId ],
              currentItemTypeName        = currentItem?.itemType.name,
              isRootNode                 = payloadReferenceId === 0,
              errorData                  = nodeIdToErrorDataMap[ ( node as ExtendTreeNode ).id ]
                  ?? ( isRootNode ? nodeIdToErrorDataMap[ 'root' ] : undefined ),
              nodeIsDeleted              = payloadReference?.isDeleted() || currentItem?.isDeleted(),
              allowedReferenceSetIds     = this.treeUtils?.getAllowedCirsIdForNode( node.id! ),
              addItemConfig              = this.getAddItemDialogConfig( currentItemTypeName, allowedReferenceSetIds, node as ExtendTreeNode ),
              allowAdd                   = !addItemConfig.disableAddExistingItem || !addItemConfig.disableAddNewItem,
              allowDelete                = !addItemConfig.disableDeleteReference || !addItemConfig.disableDeleteItem || currentItemId < 0;

        // handle DynamicNodes
        if ( node.data.payloadId ) {
            this.treeUtils?.decoratePayloadNode( node as ExtendTreeNode, undefined, elusiveData?.variables );
            const nodeIsDirty    = !node.data.ciItem.readOnly && !node.data.ciItem.ignoreDirtyMark && ( node.data.isDirty || dirtyNodeIds?.includes( node.id ?? '' ) || ( isRootNode && dirtyNodeIds?.includes( 'root' ) ) ),
                  nodeClassNames = ( node.className ?? '' ).split( ' ' );

            if ( nodeIsDirty ) {
                if ( !nodeClassNames.includes( 'yn-modified' ) ) {
                    nodeClassNames.push( 'yn-modified' );
                }
            }
            else {
                const index = nodeClassNames.findIndex( className => className === 'yn-modified' );

                if ( index !== -1 ) {
                    nodeClassNames.splice( index, 1 );
                }
            }

            node.className = nodeClassNames.join( ' ' );
        }
        else {
            this.treeUtils?.decorateTreeNode( node as ExtendTreeNode, elusiveData?.variables );
        }

        if ( !node.data.ciItem.readOnly && ( !!errorData?.count || allowAdd || allowDelete || ( runsInDesignerContext && node.data.isCyclic ) ) ) {
            retVal.push(
                <span key="label" className="yn-label" id={`${node.key}_error`}>
                    <p>
                        {parse( filterValue ? markFilterSearchesInText( nodeLabel, filterValue ) : nodeLabel )}
                        {runsInDesignerContext && node.data.isCyclic
                            ? <span className="fas fa-redo yn-danger" style={{ paddingLeft: '1em' }}></span>
                            : ''
                        }
                    </p>
                </span>
            );

            if ( errorData?.tooltip?.length ) {
                retVal.unshift(
                    <Tooltip
                        key={`#${node.key}_error_tooltip`}
                        target={`#${node.key}_error`}
                        position="right"
                        autoHide={false}
                        className="yn-tooltip"
                    >
                        {errorData.tooltip}
                    </Tooltip>
                );
            }
            if ( errorData?.count ) {
                retVal.push(
                    <span key="count" className="p-badge p-badge-no-gutter p-badge-danger">
                        {errorData.count}
                    </span>
                );
            }

            // add node button
            if ( allowAdd && !nodeIsDeleted ) {
                retVal.push(
                    this.getAddNodeButton(
                        node.id as string
                    )
                );
            }

            // delete node button
            if ( allowDelete && currentItemId !== rootItemId ) {
                retVal.push(
                    this.getDeleteNodeButton(
                        node.id as string
                    )
                );
            }
        }
        else {
            retVal.push(
                <span key="label" className="yn-label">
                    <p>
                        {parse( filterValue ? markFilterSearchesInText( nodeLabel, filterValue ) : nodeLabel )}
                    </p>
                </span>
            );
        }

        return retVal;
    }

    /**
     * Template for the toggle (expand/collapse) button of tree node.
     * This one adds the ? button for stopped nodes
     * @param node
     * @param options
     * @param items
     */
    togglerTemplate( node : ExtendTreeNode, options : TreeTogglerTemplateOptions, items : ItemCollection | undefined ) : JSX.Element {
        const label = localeOption( 'aria', '' )?.[ options.expanded ? 'collapseLabel' : 'expandLabel' ];
        const nodeStopped = node.stopped && !this.props.resumedNodes?.[ node.id! ];
        const iconClassName = classNames( 'p-tree-toggler-icon pi pi-fw', { 'pi-chevron-right': !options.expanded && !nodeStopped, 'pi-chevron-down': options.expanded && !nodeStopped, 'pi-question-circle': nodeStopped } );
        const onClick = nodeStopped
            ? ( e : React.SyntheticEvent<Element, Event> ) => {
                this.props.setParams?.( this.props.uid, {
                    resumedNodes: { ...this.props.resumedNodes, [ node.id! ]: true },
                    expandedKeys: { ...this.props.expandedKeys, [ node.id! ]: true }
                } );
                void this.resumeLoad( node as ExtendTreeNode );
            }
            : options.onClick;
        return (
            <button type="button" className="p-tree-toggler p-link" tabIndex={-1} onClick={onClick} aria-label={label}
                    style={nodeStopped ? { visibility: 'visible' } : undefined}>
                <span className={iconClassName} aria-hidden="true"></span>
                <Ripple/>
            </button> );
    }

    /**
     * Template for the tree filter input
     * @param node
     * @param options
     * @param items
     */
    filterTemplate( options : TreeFilterTemplateOptions, items : ItemCollection | undefined ) : JSX.Element {
        return (
            <div className="p-tree-filter-container">
                <SearchField
                    uid="pageQuickSearch"

                    value={options.props.filterValue}

                    onSearch={() => {
                    }}
                    onClear={() => {
                        this.props.setParam?.( this.props.uid, 'filterValue', '' );
                    }}
                    onChange={( searchString ) => {
                        this.props.setParam?.( this.props.uid, 'filterValue', searchString );
                    }}

                    placeholder={options.props.filterPlaceholder}
                    disabled={options.props.disabled}
                    autoComplete="off"
                    useHookForm={true}

                    style={{ display: 'inline-block', marginBottom: '0', width: '100%' }}
                />
            </div>
        );
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  className,
                  dataContext,
                  expandedKeys,
                  filter,
                  filterValue,
                  selectedPayloadId,
                  selectedPayloadReferenceId,
                  treeData,
                  style
              }           = this.props as AllProps,
              {
                  contextMenuNode,
                  lastContextMenuNode
              }           = this.state,
              treeItems   = global.data[ dataContext ]?.items,
              // select root node if selectedKey isn't defined
              selectedKey = ( this.props.selectedKey ? this.props.selectedKey : undefined )
                  ?? this.treeUtils?.getDynamicNodeIdByPayloadReferenceId( selectedPayloadReferenceId )?.[ 0 ]
                  ?? this.treeUtils?.getDynamicNodeIdByPayloadId( selectedPayloadId )?.[ 0 ]
                  ?? treeData?.[ 0 ]?.key;

        const retVal = <>
            <ContextMenu
                model={this.getTreeContextMenu( lastContextMenuNode || contextMenuNode )}
                ref={cm => this.cm = cm}
                onHide={() => this.setState( { contextMenuNode: '' } )}
            />
            <ConfirmPopup content=""/>
            <Tree
                ref={el => this.tree = el}

                className={`yn-menu-tree-lines ${className}`}
                contentClassName="yn-tree-container"
                style={style}

                value={treeData}

                filter={filter}
                filterValue={filterValue ?? ''}
                filterMode="lenient"
                onFilterValueChange={() => {
                    // DO NOT REMOVE - without handler filtering won't work
                }}

                nodeTemplate={( node : TreeNode.TreeNode, options : TreeNodeTemplateOptions ) => this.nodeTemplate( node, options, treeItems, filterValue )}
                togglerTemplate={( node : TreeNode.TreeNode, options : TreeTogglerTemplateOptions ) => this.togglerTemplate( node as ExtendTreeNode, options, treeItems )}
                filterTemplate={( options : TreeFilterTemplateOptions ) => this.filterTemplate( options, treeItems )}

                // the pick is necessary as PrimeReact ignores value of expandedKeys object, and we need collapsed node to false
                // as we need to detect what's different from initial loaded state
                expandedKeys={_.pickBy( expandedKeys, value => value )}
                onToggle={this.onToggleTreeNode}

                selectionMode="single"
                selectionKeys={selectedKey}
                onSelectionChange={this.onSelectTreeNode}

                contextMenuSelectionKey={contextMenuNode}
                onContextMenuSelectionChange={e => {
                    this.setState( {
                        contextMenuNode    : e.value + '',
                        lastContextMenuNode: e.value + ''
                    } );
                }}
                onContextMenu={e => this.cm?.show( e.originalEvent )}

                dragdropScope={dataContext}
                onDragStart={this.onDragStart}
                onDragEnd={this.onDragEnd}
                onValidateDrop={this.onValidateDrop}
                onDragDrop={this.onDrop}
                onDragOver={this.onDragOver}

                onNodeDoubleClick={this.onNodeDoubleClick}
            />
        </>;

        if ( this.debug ) {
            console.log( `ItemTree - render - ${Math.round( 1000 * ( performance.now() - this.renderStartTime ) ) / 1000} ms`.asLogLine( 100 ) );
            this.renderStartTime = 0;
        }

        return retVal;
    }
}

// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {};

    if ( ownProps.uid ) {
        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }
        if ( ownProps.useElusiveDataFromDataContext === false && state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }
    }

    if ( ownProps.useElusiveDataFromDataContext !== false && state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = state.elusiveData[ ownProps.dataContext ];
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

// apply mixin to ItemTree component
Object.assign( ItemTree.prototype, ActionMixin );

//export default connect( mapStateToProps, mapDispatchToProps )( ItemTree );
export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true } )( ItemTree );
